import React from 'react';
import { useParams } from 'react-router-dom';
import { Paper, Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { CheckCircleRounded, ViewAgendaRounded, StorefrontRounded, BlockRounded } from '@mui/icons-material';
import { Crumbs, DetailHead, TabBar, KV, StatBox, Grid, useTab } from '../../components/common';
import DataTable, { Column } from '../../components/DataTable';
import StatCard from '../../components/StatCard';
import StatusChip from '../../components/StatusChip';
import { Avatar } from '../../components/NameCell';
import { useConsumerDetail } from '../../hooks/useConsumers';
import { statusLabel } from '../../utils/format';
import { colors } from '../../theme/theme';

const ConsumerDetailPage: React.FC = () => {
  const { id } = useParams();
  const { data, loading, error, reload } = useConsumerDetail(id);
  const [tab, setTab] = useTab('history');

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}><CircularProgress /></Box>;
  }
  if (error || !data) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>{error || 'Not found.'}</Alert>;
  }

  const c = data.consumer;
  const s = data.stats;

  const histCols: Column<any>[] = [
    { key: 'number', label: 'Token #', render: (r) => <Typography component="span" sx={{ fontWeight: 600 }}>{r.number}</Typography> },
    { key: 'business', label: 'Business' },
    { key: 'queue', label: 'Queue' },
    { key: 'date', label: 'Date' },
    { key: 'time', label: 'Time' },
    { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} label={statusLabel(r.status)} /> },
  ];

  return (
    <>
      <Crumbs items={[{ label: 'Consumers', to: '/consumers' }, { label: c.name }]} />
      <DetailHead title="Consumer Details" actions={<StatusChip status={c.status} label={statusLabel(c.status)} />} />

      <Paper sx={{ p: 0, mb: 2.25, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1.3fr 1fr' }, '& > div': { p: 2.75 }, '& > div + div': { borderLeft: { md: `1px solid ${colors.border}` }, borderTop: { xs: `1px solid ${colors.border}`, md: 'none' } } }}>
        <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
          <Avatar name={c.name} size={64} />
          <Box>
            <Typography sx={{ fontSize: 19, fontWeight: 800 }}>{c.name}</Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 13, fontFamily: 'ui-monospace, monospace' }}>{String(c.id).slice(0, 8)} · {c.city}</Typography>
            <Box sx={{ mt: 1 }}><StatusChip status={c.status} label={statusLabel(c.status)} /></Box>
          </Box>
        </Box>
        <Box>
          <KV k="Phone" v={c.phone} />
          <KV k="Email" v={c.email} />
          <KV k="City" v={c.city} />
          <KV k="Total Tokens" v={s.total} />
          <KV k="Member Since" v={c.member_since} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>Summary</Typography>
          <StatBox>
            <KV dashed k="Completed" v={s.completed} />
            <KV dashed k="Cancelled" v={s.cancelled} />
            <KV dashed k="No Show" v={s.no_show} />
            <KV k="Businesses Visited" v={s.businesses_visited} />
          </StatBox>
        </Box>
      </Paper>

      <Grid cols={4} sx={{ mb: 2.25 }}>
        <StatCard icon={<ViewAgendaRounded />} color="#076FF7" value={s.total} label="Total Tokens" />
        <StatCard icon={<CheckCircleRounded />} color="#16A34A" value={s.completed} label="Completed" />
        <StatCard icon={<BlockRounded />} color="#EF4444" value={s.no_show} label="No Shows" />
        <StatCard icon={<StorefrontRounded />} color="#7C3AED" value={s.businesses_visited} label="Businesses Visited" />
      </Grid>

      <TabBar active={tab} onChange={setTab} tabs={[{ key: 'history', label: `Token History (${data.tokens.length})` }, { key: 'consult', label: `Consultations (${data.consultations.length})` }]} />

      {tab === 'history' && <Paper sx={{ p: 0 }}><DataTable columns={histCols} rows={data.tokens} emptyText="No tokens." /></Paper>}

      {tab === 'consult' && (
        <Box>
          {data.consultations.length ? data.consultations.map((cn, i) => (
            <Paper key={i} sx={{ p: 2, mb: 1.75 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{cn.business}</Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: 12.5 }}>Seen by {cn.doctor}</Typography>
                </Box>
                <Typography sx={{ color: 'text.secondary', fontSize: 12.5 }}>{cn.date}</Typography>
              </Box>
              <KV k="Medications" v={cn.meds?.length ? <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>{cn.meds.map((m: string) => <StatusChip key={m} status="info" label={m} />)}</Box> : '—'} />
              <KV k="Tests" v={cn.tests?.length ? <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>{cn.tests.map((t: string) => <StatusChip key={t} status="warning" label={t} />)}</Box> : '—'} />
              <KV k="Follow-up" v={cn.follow_up || '—'} />
              {cn.notes && <KV k="Notes" v={<Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>{cn.notes}</Box>} />}
            </Paper>
          )) : <Paper sx={{ p: 4 }}><Typography sx={{ color: 'text.secondary', fontSize: 13.5, textAlign: 'center' }}>No consultations.</Typography></Paper>}
        </Box>
      )}
    </>
  );
};

export default ConsumerDetailPage;
